// @ts-strict-ignore
import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, map, Observable, ReplaySubject, Subject } from 'rxjs';
import { Language } from '../../model/enums/language';
import { EditorTreeType } from '../editor-tree-service/editor-tree.constants';
import { WINDOW } from '../window.service';
import {
  ConsentOrPreferencesCookie,
  CookieConsentCookie,
  CookieName,
  CookieType,
  Preference,
  PreferencesCookie,
  PreferenceValue,
} from './app-cookie.types';

@Injectable({ providedIn: 'root' })
export class AppCookieService {
  isOpen: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _consentSubject: ReplaySubject<CookieConsentCookie>;
  private _cookieQueue: Array<{ name: CookieName; value: CookieConsentCookie | PreferencesCookie }>;
  private _currentDomain: string;
  private _openDialogRequest: Subject<boolean>;
  private _openedViaClick: boolean = false;
  private _userLanguage: Language;
  private readonly _version: number;

  clearCookies(): void {
    this._clearCookiesIfNecessary(this._getCookie(CookieName.Consent), true);
  }

  closeDialog() {
    this.isOpen.next(false);
  }

  get consentCookie$(): Observable<CookieConsentCookie | null> {
    return this._consentSubject.asObservable();
  }

  get consentedToPerformanceCookie(): Observable<boolean> {
    return this._consentSubject.pipe(map((c) => c.performance));
  }

  get openDialogRequest(): Observable<boolean> {
    return this._openDialogRequest;
  }

  constructor(
    private _cookieService: CookieService,
    @Inject(WINDOW) private _window: Window
  ) {
    this._consentSubject = new ReplaySubject(1);
    this._cookieQueue = [];
    this._currentDomain = this._window.location.hostname;
    this._openDialogRequest = new Subject();
    this._openDialogRequest.next(false);
    this._version = 2; // Bumped 2022-12-13
    if (this.hasConsented()) {
      this._consentSubject.next(this._getCookie(CookieName.Consent));
    } else {
      this._clearCookiesIfNecessary(this._getCookie(CookieName.Consent));
    }
  }

  getConsentCookie(): CookieConsentCookie | null {
    return this._getCookie(CookieName.Consent);
  }

  getDefaultCookie(): CookieConsentCookie {
    return { preferences: false, performance: false, type: CookieType.Required, version: 2 };
  }

  getPreferenceInventoryTree(): EditorTreeType.GPC | EditorTreeType.ActivityHierarchy | string | undefined {
    return this._getCookie(CookieName.Preferences)?.inventoryTree;
  }

  getUserLanguage(): Language | undefined {
    return this._getCookie(CookieName.Preferences)?.userLanguage;
  }

  hasConsented(): boolean {
    const consentCookie = this._getCookie(CookieName.Consent);
    return !!consentCookie && consentCookie.version >= this._version;
  }

  hasConsentedToToC(): boolean {
    return !!this._getCookie(CookieName.Preferences)?.consentedToToC;
  }

  hasConsentedToPerformance(): boolean {
    return this.hasConsented() && !!this._getCookie(CookieName.Consent)?.performance;
  }

  hasShownOnboarding(): boolean {
    return !!this._getCookie(CookieName.Preferences)?.shownOnboarding;
  }

  openDialogue(openedViaClick: boolean) {
    this._openedViaClick = openedViaClick;
    this._openDialogRequest.next(this._openedViaClick);
    this.isOpen.next(true);
  }

  setConsent(preferences: boolean, performance: boolean): void {
    const consentCookie: CookieConsentCookie = {
      preferences,
      performance,
      type: CookieType.Required,
      version: this._version,
    };
    this._storeJsonCookie(CookieName.Consent, consentCookie, this._getNewExpirationDate());
    if (this._cookieQueue.length) {
      this._cookieQueue.forEach((c) => {
        this._setCookie(c.name, c.value);
      });
      this._cookieQueue = [];
    }
    if (preferences && !this._getCookie(CookieName.Preferences)) {
      const userLanguage = this._userLanguage;
      const type = CookieType.Preferences;
      this._storeJsonCookie(CookieName.Preferences, { userLanguage, type });
    }
    this._clearCookiesIfNecessary(consentCookie);
    this._consentSubject.next(consentCookie);
  }

  setPreference<T extends Preference>(preference: T, value?: PreferenceValue<T>): void {
    const preferencesCookie: PreferencesCookie = {
      ...this._getCookie(CookieName.Preferences),
      type: CookieType.Preferences,
      [preference]: value,
    };
    this._setCookie(CookieName.Preferences, preferencesCookie);

    if (this._isUserLanguage(preference, value)) {
      this._userLanguage = value;
    }
  }

  private _clearCookiesIfNecessary(c: CookieConsentCookie, forceClear: boolean = false): void {
    const isCurrentVersionOrAbove = c?.version >= this._version;
    const performanceCookies = ['_ga', '_gi'];
    const necessaryCookies = ['ai_', 'ARRAffinity'];
    const isPerformance = (name: string) => performanceCookies.some((cookie) => name.startsWith(cookie));
    const isPreference = (name: string) => name === CookieName.Preferences;
    const isNecessary = (name: string) => necessaryCookies.some((cookie) => name.startsWith(cookie));
    const shouldClear = (name: string) => {
      if (isNecessary(name)) {
        return false;
      }
      if (forceClear) {
        return true;
      }
      if (isPerformance(name) && (!c?.performance || !isCurrentVersionOrAbove)) {
        return true;
      }
      if (isPreference(name) && (!c?.preferences || !isCurrentVersionOrAbove)) {
        return true;
      }
      //TODO: should we clear all unknown cookies here?
      return false;
    };
    const all = this._cookieService.getAll();
    for (let key in all) {
      if (all.hasOwnProperty(key)) {
        if (shouldClear(key)) {
          this._cookieService.delete(key, '/');
          this._cookieService.delete(key, '/', '.' + this._currentDomain); //cookies not set by us is/was prefixed by a '.'
        }
      }
    }
  }

  private _getCookie<T extends CookieName>(name: T): ConsentOrPreferencesCookie<T> {
    if (this._cookieService.check(name)) {
      const cookieString = this._cookieService.get(name);
      return JSON.parse(cookieString);
    }
    return null;
  }

  private _getNewExpirationDate(): Date {
    //Consent cookie CANNOT be longer than one year.
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    return expirationDate;
  }

  private _hasConsentedToType(type: CookieType): boolean {
    if (!this.hasConsented()) {
      return false;
    }
    if (type === CookieType.Preferences || type === CookieType.Performance) {
      return this._getCookie(CookieName.Consent)?.[type];
    }
  }

  private _isUserLanguage(preference: Preference, value: unknown): value is Language {
    return preference === 'userLanguage' && typeof value === 'string';
  }

  private _setCookie<T extends CookieName>(name: T, value: ConsentOrPreferencesCookie<T>): void {
    if (!this.hasConsented()) {
      this._cookieQueue.push({ name, value });
    } else if (this._hasConsentedToType(value.type)) {
      this._storeJsonCookie(name, value);
    }
  }

  private _storeJsonCookie(name: string, value: CookieConsentCookie | PreferencesCookie, expirationDate?: Date): void {
    const stringifiedCookie = JSON.stringify(value);
    expirationDate ??= this._getNewExpirationDate();
    this._cookieService.set(name, stringifiedCookie, expirationDate, '/', undefined, true, 'None'); // SameSite=None;Secure required when running the app inside an iframe
  }
}
